import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Same Vite + react-pdf 9 worker pattern used by PdfViewer.tsx; idempotent at module scope.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Icon } from '../../components/Icon.tsx';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/** A file queued for merge. Structural — mirrors the Item shape in MergePdf. */
interface PreviewItem {
  id: string;
  file: File;
}

// A4 aspect ratios (width / height), matching the server's page geometry: portrait
// pages for PDFs/tall images, landscape pages for wide images.
const A4_PORTRAIT = 595.28 / 841.89; // ≈ 0.707
const A4_LANDSCAPE = 841.89 / 595.28; // ≈ 1.414

function isPdf(f: File): boolean {
  return f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
}

/**
 * Reserves a page-sized slot immediately but only mounts its (expensive) child
 * once it scrolls near the container — so a long merge doesn't rasterise every
 * page up front. `eager` renders the very first page without waiting.
 */
function Lazy({
  width,
  aspect,
  rootRef,
  eager,
  children,
}: {
  width: number;
  aspect: number;
  rootRef: RefObject<HTMLElement>;
  eager?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(!!eager);
  const h = Math.round(width / aspect);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { root: rootRef.current ?? null, rootMargin: '600px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootRef]);

  return (
    <div ref={ref} style={{ width }} className="shrink-0">
      {show ? (
        children
      ) : (
        <div style={{ height: h }} className="rounded-md border border-dashed border-border/60 bg-muted/20" />
      )}
    </div>
  );
}

/** All pages of one PDF input, rendered in order (client-side, no upload). */
function PdfPages({
  file,
  width,
  rootRef,
  firstEager,
}: {
  file: File;
  width: number;
  rootRef: RefObject<HTMLElement>;
  firstEager: boolean;
}): JSX.Element {
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        style={{ width, height: Math.round(width / A4_PORTRAIT) }}
        className="flex shrink-0 flex-col items-center justify-center gap-2 rounded-md border border-border bg-white text-muted-foreground shadow-sm"
      >
        <Icon name="warning" size={22} />
        <span className="text-xs">Couldn’t preview this PDF</span>
      </div>
    );
  }

  return (
    // file={File}: react-pdf reads the bytes in-memory — no network fetch, so the
    // CSP's connect-src (no blob:) is never involved.
    <Document
      file={file}
      loading={null}
      error={null}
      onLoadSuccess={(p) => setNumPages(p.numPages)}
      onLoadError={() => setFailed(true)}
      className="flex w-full flex-col items-center gap-4"
    >
      {Array.from({ length: numPages }, (_, i) => (
        <Lazy key={i} width={width} aspect={A4_PORTRAIT} rootRef={rootRef} eager={firstEager && i === 0}>
          <div className="overflow-hidden rounded-md border border-border bg-white shadow-sm">
            <Page
              pageNumber={i + 1}
              width={width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={<div style={{ height: Math.round(width / A4_PORTRAIT) }} />}
            />
          </div>
        </Lazy>
      ))}
    </Document>
  );
}

/**
 * One image input as a single page — fitted onto a white A4 card, landscape if
 * the image is landscape, mirroring what the server does. `img` uses a blob:
 * URL, which the CSP's img-src (blob: allowed) permits. Formats the browser
 * can't decode fall back to a placeholder page; they still merge on download.
 */
function ImagePage({
  file,
  width,
  rootRef,
  eager,
}: {
  file: File;
  width: number;
  rootRef: RefObject<HTMLElement>;
  eager: boolean;
}): JSX.Element {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const [aspect, setAspect] = useState(A4_PORTRAIT);
  const [failed, setFailed] = useState(false);

  return (
    <Lazy width={width} aspect={aspect} rootRef={rootRef} eager={eager}>
      <div
        style={{ width, height: Math.round(width / aspect) }}
        className="flex items-center justify-center overflow-hidden rounded-md border border-border bg-white shadow-sm"
      >
        {failed ? (
          <div className="flex flex-col items-center gap-2 p-4 text-center text-muted-foreground">
            <Icon name="hide_image" size={22} />
            <span className="text-xs">Image · will be added as a page</span>
          </div>
        ) : (
          <img
            src={url}
            alt=""
            className="max-h-full max-w-full object-contain p-2"
            onLoad={(e) => {
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
              if (w && h) setAspect(w > h ? A4_LANDSCAPE : A4_PORTRAIT);
            }}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </Lazy>
  );
}

/**
 * Live preview of the merged result, assembled entirely in the browser: each
 * file's pages are rendered in order in one continuous scroll. Because merging
 * is just page concatenation, rendering the sources in sequence is visually the
 * merged document — with zero server calls, zero uploads, and instant updates
 * when files are added, removed, or reordered.
 */
export function MergePreview({ items }: { items: PreviewItem[] }): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // Fit page width to the scroll container (minus breathing room), capped for
  // legibility. Measured synchronously before paint (so pages never briefly
  // render at a too-wide fallback and force horizontal overflow), then kept
  // fitted by a ResizeObserver as the layout changes.
  const fit = (w: number): number => Math.max(120, Math.min(760, Math.floor(w - 24)));
  // Measure once on mount, before paint. This component is only mounted once
  // there are ≥2 files (see MergePdf), so the container is present here.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el) {
      const w = el.getBoundingClientRect().width;
      if (w) setWidth(fit(w));
    }
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(fit(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (items.length < 2) return null;

  const pageWidth = width || 320;

  return (
    <section className="rounded-3xl border border-[hsl(var(--border)/0.5)] bg-[hsl(var(--surface-container-low))] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5">
        <span className="grid size-8 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
          <Icon name="visibility" size={18} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink leading-tight">Live preview</p>
          <p className="text-xs text-muted-foreground leading-tight">Updates as you reorder</p>
        </div>
      </div>

      <div className="px-3 pb-3">
        <div
          ref={containerRef}
          className="max-h-[72vh] overflow-y-auto overflow-x-clip overscroll-contain rounded-2xl bg-[hsl(var(--surface-container-high))]"
        >
          <div className="flex flex-col items-center gap-4 p-4">
            {items.map((it, idx) =>
              isPdf(it.file) ? (
                <PdfPages key={it.id} file={it.file} width={pageWidth} rootRef={containerRef} firstEager={idx === 0} />
              ) : (
                <ImagePage key={it.id} file={it.file} width={pageWidth} rootRef={containerRef} eager={idx === 0} />
              ),
            )}
          </div>
        </div>
        <p className="mt-3 px-2 text-xs text-muted-foreground">
          Assembled in your browser — uncommon image types show a placeholder here but still merge on download.
        </p>
      </div>
    </section>
  );
}
